import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addItem, delItem } from '../redux/action/index';
import "./Product.css";
import { sendRequest } from '../ApiRequest';
import { sendCachedRequest } from '../utils/apiCache';
import { BASE_URL } from '../URL/constants';
import useCurrentUser from "../components/common/UseCurrentUser";
import { FaChevronDown, FaChevronUp, FaHeart, FaStar } from 'react-icons/fa';
import { addToWishlist, removeFromWishlist } from './wishlist';

const defaultImg = "/assets/images/products/p.png";

const Product = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [priceRange, setPriceRange] = useState({ min: 0, max: 1000 });
  const [sliderValues, setSliderValues] = useState({ min: 0, max: 1000 });
  const [maxPrice, setMaxPrice] = useState(1000);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('popularity');
  const [expandedFilters, setExpandedFilters] = useState({
    categories: true,
    price: true,
    brand: false,
    discount: false
  });
  const observer = useRef();
  const searchTimeoutRef = useRef(null);
  const selectedCategoryRef = useRef(selectedCategory);
  const [wishlistIds, setWishlistIds] = useState([]);
  const [businessType, setBusinessType] = useState(null);
  const [businessCategory, setBusinessCategory] = useState(null);
  const [businessInfoLoaded, setBusinessInfoLoaded] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  
  const { isLoggedIn, currentUser } = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const reduxCartItems = useSelector((state) => state.addItem);
  const [cartItemIds, setCartItemIds] = useState(new Set());
  const categoryDropdownRef = useRef(null);
  
  // Check if design should be applied (retail + groceries/cosmetic/general_business)
  const shouldApplyDesign = businessType === "retail" && 
    (businessCategory === "groceries" || businessCategory === "cosmetic" || businessCategory === "general_business");
  
  // Function to check if item is in cart
  const isItemInCart = useCallback((itemId) => {
    // Check Redux state
    if (reduxCartItems.some(cartItem => cartItem.item_id === itemId || cartItem.id === itemId)) {
      return true;
    }
    
    // Check localStorage for guest cart
    if (!isLoggedIn) {
      const guestCart = JSON.parse(localStorage.getItem("guestCart")) || {};
      const guestCartItems = Array.isArray(guestCart) ? guestCart : Object.values(guestCart);
      return guestCartItems.some(cartItem => cartItem.item_id === itemId || cartItem.id === itemId);
    }
    
    // Check cartItemIds set
    return cartItemIds.has(itemId);
  }, [reduxCartItems, isLoggedIn, cartItemIds]);
  
  // Fetch cart items to track which items are in cart
  useEffect(() => {
    const fetchCartItemIds = async () => {
      if (!isLoggedIn || !currentUser?.data?.customer) {
        // For guest users, check localStorage
        const guestCart = JSON.parse(localStorage.getItem("guestCart")) || {};
        const guestCartItems = Array.isArray(guestCart) ? guestCart : Object.values(guestCart);
        const itemIds = new Set(guestCartItems.map(item => item.item_id || item.id).filter(Boolean));
        setCartItemIds(itemIds);
        return;
      }
      
      try {
        const response = await sendRequest(
          'GET',
          `${BASE_URL}/v1/public_site/customers/${currentUser.data.customer.id}/cart`,
          null,
          { Authorization: currentUser.data.token }
        );
        const apiCartItems = response.data.cart_items || [];
        const itemIds = new Set(apiCartItems.map(item => item.item_id).filter(Boolean));
        setCartItemIds(itemIds);
      } catch (error) {
        console.error('Error fetching cart items:', error);
      }
    };
    
    fetchCartItemIds();
    
    // Also listen to cart-updated events
    const handleCartUpdated = () => {
      fetchCartItemIds();
    };
    
    window.addEventListener('cart-updated', handleCartUpdated);
    return () => {
      window.removeEventListener('cart-updated', handleCartUpdated);
    };
  }, [isLoggedIn, currentUser]);

  const fetchItems = async (categoryName = '', page = 1, reset = false, searchQueryParam = null) => {
    try {
      setIsLoading(true);
      
      // Use provided searchQueryParam or fall back to state
      const normalizedSearch = searchQueryParam !== null ? searchQueryParam.trim() : searchQuery.trim();
      let url = `${BASE_URL}/v1/public_site/menus/item_lists?category_name=${categoryName}&page=${page}&per_page=20&devise_type=web&min_price=${priceRange.min}&max_price=${priceRange.max}`;
      
      // Add search query if present
      if (normalizedSearch) {
        url += `&query=${encodeURIComponent(normalizedSearch)}`;
      }
      
      const response = await sendCachedRequest('GET', url, null, {
        cacheKey: `items:${categoryName}:${page}:${priceRange.min}:${priceRange.max}:${normalizedSearch}`,
        cacheDuration: 60 * 1000,
      });
      
      if (reset || page === 1) {
        setItems(response.data.items || []);
      } else {
        setItems(prevItems => [...prevItems, ...(response.data.items || [])]);
      }
      
      setTotalItems(response.data.total_count || 0);
      
      // Check if there are more pages
      const totalPages = response.data.total_pages || 1;
      const currentPageNum = response.data.current_page || page;
      setHasMore(currentPageNum < totalPages && (response.data.items?.length || 0) > 0);
      
      // Calculate max price from all items
      if (response.data.items && response.data.items.length > 0) {
        const prices = response.data.items.map(item => parseFloat(item.sales_price) || 0);
        const currentMax = Math.max(...prices, maxPrice);
        if (currentMax > maxPrice) {
          setMaxPrice(Math.ceil(currentMax / 100) * 100); // Round up to nearest 100
        }
      }
    } catch (error) {
      console.error('Failed to fetch items:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch all items once to get max price
  const fetchMaxPrice = async () => {
    try {
      const response = await sendRequest('GET', `${BASE_URL}/v1/public_site/menus/item_lists?devise_type=web`);
      if (response.data.items && response.data.items.length > 0) {
        const prices = response.data.items.map(item => parseFloat(item.sales_price) || 0);
        const calculatedMax = Math.max(...prices);
        const roundedMax = Math.ceil(calculatedMax / 100) * 100; // Round up to nearest 100
        setMaxPrice(roundedMax || 1000);
        setPriceRange({ min: 0, max: roundedMax || 1000 });
        setSliderValues({ min: 0, max: roundedMax || 1000 });
      }
    } catch (error) {
      console.error('Failed to fetch max price:', error);
    }
  };




  const clearSearch = () => {
    setSearchQuery('');
    
    // Clear any pending search timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Clear header search input as well
    const headerSearchInput = document.querySelector('.search-box input[type="text"]');
    if (headerSearchInput) {
      headerSearchInput.value = '';
    }
    localStorage.removeItem('headerSearchQuery');
    
    // Reset to normal items view
    setCurrentPage(1);
    fetchItems(selectedCategory, 1, true, '');
  };

  const fetchCategories = async () => {
    try {
      const response = await sendCachedRequest(
        'GET',
        `${BASE_URL}/v1/public_site/menus/fetch_categories?devise_type=web`,
        null,
        { cacheKey: 'menu_categories' }
      );
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  // Fetch wishlist items once for logged-in user
  useEffect(() => {
    const fetchWishlistIds = async () => {
      try {
        const token = currentUser?.data?.token;
        if (!token) return;
        const result = await sendRequest(
          'GET',
          `${BASE_URL}/v1/public_site/wishlists`,
          null,
          { Authorization: token }
        );
        const wishlistItems = result.data || [];
        setWishlistIds(wishlistItems.map((w) => w.item_id));
      } catch (err) {
        console.error('Failed to fetch wishlist items:', err);
      }
    };

    if (isLoggedIn && currentUser?.data?.token) {
      fetchWishlistIds();
    } else {
      setWishlistIds([]);
    }
  }, [isLoggedIn, currentUser]);

  // Fetch business type and category
  useEffect(() => {
    const fetchBusinessInfo = async () => {
      try {
        const response = await sendCachedRequest(
          'POST',
          `${BASE_URL}/v1/public_site/public_site_info/get_list`,
          null,
          { cacheKey: 'public_site_info' }
        );
        
        const apiBusinessType = response?.data?.data?.business_type;
        const apiBusinessCategory = response?.data?.data?.business_category;
        
        setBusinessType(apiBusinessType);
        setBusinessCategory(apiBusinessCategory);
        setBusinessInfoLoaded(true);
      } catch (error) {
        console.error('Error fetching business info:', error);
        setBusinessInfoLoaded(true); // Still set to true to prevent infinite loading
      }
    };

    fetchBusinessInfo();
  }, []);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const categoryParam = queryParams.get('category');
    
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    }

    fetchCategories();
  }, [location]);

  useEffect(() => {
    fetchMaxPrice();
  }, []);

  // Update ref when selectedCategory changes
  useEffect(() => {
    selectedCategoryRef.current = selectedCategory;
  }, [selectedCategory]);

  useEffect(() => {
    setCurrentPage(1);
    // Get current search query from state or localStorage
    const currentQuery = searchQuery || localStorage.getItem('headerSearchQuery') || '';
    console.log('Product.jsx - Category changed, fetching with query:', currentQuery);
    fetchItems(selectedCategory, 1, true, currentQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  useEffect(() => {
    if (selectedCategory) {
      navigate(`/products?category=${encodeURIComponent(selectedCategory)}`, { replace: true });
    } else {
      navigate('/products', { replace: true });
    }
  }, [selectedCategory, navigate]);

  useEffect(() => {
    if (currentPage > 1) {
      fetchItems(selectedCategory, currentPage);
    }
  }, [currentPage]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Listen to header search events - stable listener
  useEffect(() => {
    const handleHeaderSearch = (event) => {
      const query = event.detail?.query || '';
      console.log('Product.jsx - Header search event received:', query, 'Event:', event);
      
      // Clear any pending search timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      // Store in localStorage
      if (query && query.trim()) {
        localStorage.setItem('headerSearchQuery', query.trim());
      } else {
        localStorage.removeItem('headerSearchQuery');
      }
      
      // Update search query state and immediately fetch
      setSearchQuery(query);
      setCurrentPage(1);
      
      // Get current category from ref (always has latest value)
      const currentCategory = selectedCategoryRef.current || '';
      
      // Force immediate fetch with the query (pass query as parameter to avoid state timing issues)
      console.log('Product.jsx - Fetching items with query:', query, 'category:', currentCategory);
      fetchItems(currentCategory, 1, true, query);
    };

    // Use capture phase to ensure we catch the event
    window.addEventListener('header-search', handleHeaderSearch, true);
    console.log('Product.jsx - Event listener attached (capture phase)');

    // Load saved search query on mount
    const savedQuery = localStorage.getItem('headerSearchQuery');
    if (savedQuery && savedQuery.trim()) {
      console.log('Product.jsx - Loading saved search query:', savedQuery);
      setSearchQuery(savedQuery);
      setCurrentPage(1);
      fetchItems(selectedCategoryRef.current || '', 1, true, savedQuery);
    }

    return () => {
      window.removeEventListener('header-search', handleHeaderSearch, true);
      console.log('Product.jsx - Event listener removed');
    };
  }, []); // Empty dependency array - listener stays stable

  // Fetch items when searchQuery changes (triggered by header search or local search input)
  useEffect(() => {
    console.log('Product.jsx - searchQuery changed, fetching items:', searchQuery);
    fetchItems(selectedCategory, 1, true, searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, priceRange]);

  const lastItemRef = useCallback(node => {
    if (isLoading || !hasMore) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !isLoading) {
        setCurrentPage(prevPage => prevPage + 1);
      }
    });
    
    if (node) observer.current.observe(node);
  }, [isLoading, hasMore]);

  const toggleCartItem = async (item) => {
    const isInCart = isItemInCart(item.id);
    
    if (isInCart) {
      // Remove from cart
      await removeFromCart(item);
    } else {
      // Add to cart
      await addToCart(item);
    }
  };
  
  const addToCart = async (item) => {
    if (!isLoggedIn) {
      const guestCart = JSON.parse(localStorage.getItem("guestCart")) || {};
      const cartItemId = `guest_${Date.now()}`;

      const cartItem = {
        id: cartItemId,
        item_id: item.id,
        item_name: item.item_name,
        sales_price: item.sales_price,
        sales_discount: item.sales_discount || 0,
        img: item.item_images?.[0] || defaultImg,
        quantity: 1
      };

      guestCart[cartItemId] = cartItem;
      localStorage.setItem("guestCart", JSON.stringify(guestCart));
      
      // Dispatch Redux action for guest cart
      dispatch(addItem(cartItem));
      
      // Update cart item IDs
      setCartItemIds(prev => new Set([...prev, item.id]));
      
      // Dispatch custom event for header cart badge update
      window.dispatchEvent(new Event('cart-updated'));
      return;
    }

    try {
      const response = await sendRequest(
        'POST',
        `${BASE_URL}/v1/public_site/customers/${currentUser.data.customer.id}/cart/cart_items`,
        { item_id: item.id, quantity: 1 },
        { Authorization: currentUser.data.token }
      );
      
      // Dispatch Redux action for logged-in user cart
      if (response.data) {
        const cartItem = {
          id: response.data.id || item.id,
          item_id: item.id,
          item_name: item.item_name,
          sales_price: item.sales_price,
          sales_discount: item.sales_discount || 0,
          img: item.item_images?.[0] || defaultImg,
          quantity: 1
        };
        dispatch(addItem(cartItem));
        
        // Update cart item IDs
        setCartItemIds(prev => new Set([...prev, item.id]));
        
        // Dispatch custom event for header cart badge update
        window.dispatchEvent(new Event('cart-updated'));
      }
    } catch (error) {
      console.error('Error adding item to cart:', error);
      alert('Failed to add item to cart');
    }
  };
  
  const removeFromCart = async (item) => {
    if (!isLoggedIn) {
      const guestCart = JSON.parse(localStorage.getItem("guestCart")) || {};
      const guestCartItems = Array.isArray(guestCart) ? guestCart : Object.values(guestCart);
      
      // Find and remove all matching items (by item_id or id)
      const updatedCart = guestCartItems.filter(cartItem => {
        const cartItemId = cartItem.item_id || cartItem.id;
        const itemId = item.item_id || item.id;
        return cartItemId !== itemId;
      });
      
      // Convert back to object format if needed
      const updatedCartObj = {};
      updatedCart.forEach(cartItem => {
        const cartItemId = cartItem.id || `guest_${Date.now()}_${Math.random()}`;
        updatedCartObj[cartItemId] = cartItem;
      });
      
      localStorage.setItem("guestCart", JSON.stringify(updatedCartObj));
      
      // Remove from Redux - remove all matching items
      const itemsToRemove = reduxCartItems.filter(cartItem => {
        const cartItemId = cartItem.item_id || cartItem.id;
        const itemId = item.item_id || item.id;
        return cartItemId === itemId;
      });
      
      itemsToRemove.forEach(cartItem => {
        dispatch(delItem(cartItem));
      });
      
      // Update cart item IDs
      setCartItemIds(prev => {
        const newSet = new Set(prev);
        const itemId = item.item_id || item.id;
        newSet.delete(itemId);
        return newSet;
      });
      
      // Dispatch custom event for header cart badge update
      window.dispatchEvent(new Event('cart-updated'));
      return;
    }

    try {
      // For logged-in users, fetch cart first to get the actual cart_item
      const cartResponse = await sendRequest(
        'GET',
        `${BASE_URL}/v1/public_site/customers/${currentUser.data.customer.id}/cart`,
        null,
        { Authorization: currentUser.data.token }
      );
      
      const apiCartItems = cartResponse.data?.cart_items || [];
      const cartItemToDelete = apiCartItems.find(cartItem => 
        cartItem.item_id === item.id
      );
      
      if (cartItemToDelete) {
        // Use item_id for deletion (backend handles both item_id and cart_item id)
        await sendRequest(
          'DELETE',
          `${BASE_URL}/v1/public_site/customers/${currentUser.data.customer.id}/cart/cart_items/${cartItemToDelete.item_id}`,
          null,
          { Authorization: currentUser.data.token }
        );
      }
      
      // Remove from Redux - remove all matching items
      const itemsToRemove = reduxCartItems.filter(cartItem => 
        cartItem.item_id === item.id || (cartItem.id === item.id && cartItem.item_id === item.id)
      );
      
      itemsToRemove.forEach(cartItem => {
        dispatch(delItem(cartItem));
      });
      
      // Update cart item IDs
      setCartItemIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.id);
        return newSet;
      });
      
      // Dispatch custom event for header cart badge update
      window.dispatchEvent(new Event('cart-updated'));
    } catch (error) {
      console.error('Error removing item from cart:', error);
      // Even if API call fails, update local state
      const itemsToRemove = reduxCartItems.filter(cartItem => 
        cartItem.item_id === item.id || (cartItem.id === item.id && cartItem.item_id === item.id)
      );
      
      itemsToRemove.forEach(cartItem => {
        dispatch(delItem(cartItem));
      });
      
      setCartItemIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.id);
        return newSet;
      });
      
      window.dispatchEvent(new Event('cart-updated'));
      alert('Failed to remove item from cart. Please try again.');
    }
  };

  const toggleWishlistItem = async (item) => {
    if (!isLoggedIn || !currentUser?.data?.token) {
      // If not logged in, send user to login
      navigate('/login');
      return;
    }

    const token = currentUser.data.token;
    const isInWishlist = wishlistIds.includes(item.id);

    try {
      if (isInWishlist) {
        await removeFromWishlist(item.id, token);
        setWishlistIds((prev) => prev.filter((id) => id !== item.id));
      } else {
        await addToWishlist(item.id, token);
        setWishlistIds((prev) => [...prev, item.id]);
      }
    } catch (error) {
      console.error('Error updating wishlist:', error);
      alert('Failed to update wishlist. Please try again.');
    }
  };


  const handleCategoryChange = (eventOrName) => {
    const value =
      typeof eventOrName === 'string'
        ? eventOrName
        : eventOrName?.target?.value ?? '';
    setSelectedCategory(value);
    setCurrentPage(1);
    const currentQuery = searchQuery || localStorage.getItem('headerSearchQuery') || '';
    fetchItems(value, 1, true, currentQuery);
  };

  const handleSliderChange = (event) => {
    const { name, value } = event.target;
    const numValue = Number(value);
    
    setSliderValues(prev => {
      // Ensure min doesn't exceed max and max doesn't go below min
      if (name === 'min') {
        return {
          ...prev,
          min: Math.min(numValue, prev.max)
        };
      } else {
        return {
          ...prev,
          max: Math.max(numValue, prev.min)
        };
      }
    });
  };

  // Auto-apply price filter when slider changes
  useEffect(() => {
    const timer = setTimeout(() => {
      if (priceRange.min !== sliderValues.min || priceRange.max !== sliderValues.max) {
        setPriceRange(sliderValues);
        setCurrentPage(1);
        const currentQuery = searchQuery || localStorage.getItem('headerSearchQuery') || '';
        fetchItems(selectedCategory, 1, true, currentQuery);
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sliderValues]);

  // Close custom category dropdown when clicking outside
  useEffect(() => {
    if (!isCategoryDropdownOpen) return;
    const handleClickOutside = (event) => {
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target)
      ) {
        setIsCategoryDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isCategoryDropdownOpen]);

  const goToProductDetail = (itemId) => {
    navigate(`/products/${itemId}`);
  };

  const filteredItems = items.filter(item => {
    const matchesPrice = item.sales_price >= priceRange.min && item.sales_price <= priceRange.max;
    return matchesPrice;
  }).sort((a, b) => {
    switch(sortBy) {
      case 'price-low':
        return (a.sales_price - a.sales_discount) - (b.sales_price - b.sales_discount);
      case 'price-high':
        return (b.sales_price - b.sales_discount) - (a.sales_price - a.sales_discount);
      case 'newest':
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      case 'discount':
        return (b.sales_discount || 0) - (a.sales_discount || 0);
      default:
        return 0;
    }
  });

  const toggleFilter = (filterName) => {
    setExpandedFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };

  const calculateDiscount = (item) => {
    if (item.sales_discount > 0 && item.sales_price > 0) {
      return Math.round((item.sales_discount / item.sales_price) * 100);
    }
    return 0;
  };

  const cardItem = (item, index) => {
    const isLastItem = index === filteredItems.length - 1;
    const discount = calculateDiscount(item);
    const isWishlisted = isLoggedIn && wishlistIds.includes(item.id);
    const finalPrice = item.sales_price - (item.sales_discount || 0);
    const itemInCart = isItemInCart(item.id);
    
    return (
      <div 
        className="product-card" 
        key={`${item.id}-${index}`} 
        ref={isLastItem ? lastItemRef : null}
      >
        <div className="product-tumb">
          {discount > 0 && (
            <div className="discount-badge">{discount}% OFF</div>
          )}
          <button
            className={`wishlist-icon ${isWishlisted ? 'active' : ''}`}
            onClick={() => toggleWishlistItem(item)}
            type="button"
          >
            <FaHeart />
          </button>
          <img
            src={item.item_images?.[0] || defaultImg}
            alt={item.item_name}
            onClick={() => goToProductDetail(item.id)}
          />
        </div>
        <div className="product-details">
          <h4 onClick={() => goToProductDetail(item.id)}>{item.item_name}</h4>
          <div className="product-rating">
            <div className="stars">
              {[1, 2, 3, 4, 5].map(star => (
                <FaStar key={star} className={star <= 4 ? 'filled' : 'empty'} />
              ))}
            </div>
            <span className="rating-text">(4.2)</span>
          </div>
          <div className="product-price">
            {item.sales_discount > 0 ? (
              <>
                <span className="current-price">₹{parseFloat(finalPrice).toLocaleString()}</span>
                <span className="original-price">₹{parseFloat(item.sales_price).toLocaleString()}</span>
                {discount > 0 && (
                  <span className="discount-percent">{discount}% off</span>
                )}
              </>
            ) : (
              <span className="current-price">₹{parseFloat(item.sales_price).toLocaleString()}</span>
            )}
          </div>
          <div className="product-bottom-details">
            <div className="product-links">
              <button
                className={`add-to-cart-btn ${itemInCart ? 'remove-from-cart' : ''}`}
                onClick={() => toggleCartItem(item)}
              >
                {itemInCart ? 'Remove from Cart' : 'Add to Cart'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Wait for business info to load before rendering
  if (!businessInfoLoaded) {
    return (
      <div className="product-page-wrapper">
        <div className="loading-spinner" style={{ textAlign: 'center', padding: '50px' }}>
          Loading...
        </div>
      </div>
    );
  }

  // Render different design based on business type and category
  if (shouldApplyDesign) {
    // Retail + Groceries/Cosmetic/General Business Design
    return (
      <div className="product-page-wrapper retail-groceries-design">
        {/* Breadcrumbs */}
        <nav className="breadcrumb-nav">
          <ol className="breadcrumb">
            <li><NavLink to="/">Home</NavLink></li>
            <li className="separator">›</li>
            <li className="active">{selectedCategory || 'All Products'}</li>
          </ol>
        </nav>

        {/* Main Container with Sidebar and Content */}
        <div className="product-container">
        {/* Left Sidebar - Filters */}
        <div className="filter-sidebar">
          <div className="filters-header">
            <h2>Filters</h2>
          </div>

          {/* Categories Filter */}
          <div className="filter-section">
            <div className="filter-header" onClick={() => toggleFilter('categories')}>
              <span className="filter-title">CATEGORIES</span>
              {expandedFilters.categories ? <FaChevronUp /> : <FaChevronDown />}
            </div>
            {expandedFilters.categories && (
              <div className="filter-content">
                <div
                  className="category-dropdown"
                  ref={categoryDropdownRef}
                >
                  <button
                    type="button"
                    className="category-dropdown-toggle"
                    onClick={() => setIsCategoryDropdownOpen((open) => !open)}
                  >
                    <span className="category-dropdown-label">
                      {selectedCategory || 'All Categories'}
                    </span>
                    <span className="category-dropdown-icon">▾</span>
                  </button>
                  {isCategoryDropdownOpen && (
                    <ul className="category-dropdown-menu">
                      <li
                        className={`category-dropdown-item ${
                          !selectedCategory ? 'active' : ''
                        }`}
                        onClick={() => {
                          handleCategoryChange('');
                          setIsCategoryDropdownOpen(false);
                        }}
                      >
                        All Categories
                      </li>
                      {categories.map((category) => (
                        <li
                          key={category.id}
                          className={`category-dropdown-item ${
                            selectedCategory === category.name ? 'active' : ''
                          }`}
                          onClick={() => {
                            handleCategoryChange(category.name);
                            setIsCategoryDropdownOpen(false);
                          }}
                        >
                          {category.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Price Filter */}
          <div className="filter-section">
            <div className="filter-header" onClick={() => toggleFilter('price')}>
              <span className="filter-title">PRICE</span>
              {expandedFilters.price ? <FaChevronUp /> : <FaChevronDown />}
            </div>
            {expandedFilters.price && (
              <div className="filter-content">
                <div className="price-range">
                  <div className="price-input">
                    <div className="price-input-group">
                      <div className="price-input-wrapper">
                        <span className="currency">₹</span>
                        <input type="number" value={sliderValues.min} readOnly />
                      </div>
                      <span className="price-separator">to</span>
                      <div className="price-input-wrapper">
                        <span className="currency">₹</span>
                        <input type="number" value={sliderValues.max} readOnly />
                        <span className="price-plus">+</span>
                      </div>
                    </div>
                  </div>
                  <div className="range-slider">
                    <div 
                      className="range-track"
                      style={{
                        '--min-percent': `${(sliderValues.min / maxPrice) * 100}`,
                        '--max-percent': `${(sliderValues.max / maxPrice) * 100}`
                      }}
                    ></div>
                    <input
                      type="range"
                      min="0"
                      max={maxPrice}
                      step="10"
                      value={sliderValues.min}
                      name="min"
                      onChange={handleSliderChange}
                      className="range-min"
                    />
                    <input
                      type="range"
                      min="0"
                      max={maxPrice}
                      step="10"
                      value={sliderValues.max}
                      name="max"
                      onChange={handleSliderChange}
                      className="range-max"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Content Area - Products */}
        <div className="product-content">
          {/* Page Header with Title and Sort */}
          <div className="content-header">
            {/* <div className="page-title-section">
              <h1 className="page-title">{selectedCategory || 'All Products'}</h1>
              <p className="product-count">
                (Showing {filteredItems.length > 0 ? '1' : '0'}-{Math.min(filteredItems.length, 40)} products of {totalItems} products)
              </p>
            </div> */}
            <div className="sort-section">
              <span className="sort-label">Sort by:</span>
              <div className="sort-options">
                <button 
                  className={`sort-option ${sortBy === 'popularity' ? 'active' : ''}`}
                  onClick={() => setSortBy('popularity')}
                >
                  Popularity
                </button>
                <button 
                  className={`sort-option ${sortBy === 'price-low' ? 'active' : ''}`}
                  onClick={() => setSortBy('price-low')}
                >
                  Price -- Low to High
                </button>
                <button 
                  className={`sort-option ${sortBy === 'price-high' ? 'active' : ''}`}
                  onClick={() => setSortBy('price-high')}
                >
                  Price -- High to Low
                </button>
                <button 
                  className={`sort-option ${sortBy === 'newest' ? 'active' : ''}`}
                  onClick={() => setSortBy('newest')}
                >
                  Newest First
                </button>
                <button 
                  className={`sort-option ${sortBy === 'discount' ? 'active' : ''}`}
                  onClick={() => setSortBy('discount')}
                >
                  Discount
                </button>
              </div>
              <p className="product-count">
                (Showing {filteredItems.length > 0 ? '1' : '0'}-{Math.min(filteredItems.length, 40)} products of {totalItems} products)
              </p>
            </div>
          </div>

          {/* Products Grid */}
          {filteredItems.length > 0 ? (
            <>
              <div className="product-grid">
                {filteredItems.map((item, index) => cardItem(item, index))}
              </div>
              {isLoading && (
                <div className="loading-more">
                  <div className="loading-spinner"></div>
                  Loading more products...
                </div>
              )}
              {!hasMore && filteredItems.length > 0 && (
                <div className="no-more-products">
                  <p>No more products to load</p>
                </div>
              )}
            </>
          ) : searchQuery ? (
            <div className="no-products">
              <div className="no-products-icon">📦</div>
              <h3>No products found</h3>
              <p>No products match your search "<strong>{searchQuery}</strong>". Try different keywords or browse all products.</p>
              <button 
                className="reset-filters"
                onClick={clearSearch}
              >
                Clear Search
              </button>
            </div>
          ) : (
            <div className="no-products">
              <div className="no-products-icon">📦</div>
              <h3>No products found</h3>
              <p>Try adjusting your filters or search for something else</p>
              <button 
                className="reset-filters"
                onClick={() => {
                  setSelectedCategory('');
                  setPriceRange({ min: 0, max: maxPrice });
                  setSliderValues({ min: 0, max: maxPrice });
                }}
              >
                Reset Filters
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
    );
  }

  // Default Design (for other business types/categories)
  return (
    <div className="product-page-wrapper">
      {/* Breadcrumbs */}
      <nav className="breadcrumb-nav">
        <ol className="breadcrumb">
          <li><NavLink to="/">Home</NavLink></li>
          <li className="separator">›</li>
          <li className="active">{selectedCategory || 'All Products'}</li>
        </ol>
      </nav>

      {/* Main Container with Sidebar and Content */}
      <div className="product-container">
        {/* Left Sidebar - Filters */}
        <div className="filter-sidebar">
          <div className="filters-header">
            <h2>Filters</h2>
          </div>

          {/* Categories Filter */}
          <div className="filter-section">
            <div className="filter-header" onClick={() => toggleFilter('categories')}>
              <span className="filter-title">CATEGORIES</span>
              {expandedFilters.categories ? <FaChevronUp /> : <FaChevronDown />}
            </div>
            {expandedFilters.categories && (
              <div className="filter-content">
                <div
                  className="category-dropdown"
                  ref={categoryDropdownRef}
                >
                  <button
                    type="button"
                    className="category-dropdown-toggle"
                    onClick={() => setIsCategoryDropdownOpen((open) => !open)}
                  >
                    <span className="category-dropdown-label">
                      {selectedCategory || 'All Categories'}
                    </span>
                    <span className="category-dropdown-icon">▾</span>
                  </button>
                  {isCategoryDropdownOpen && (
                    <ul className="category-dropdown-menu">
                      <li
                        className={`category-dropdown-item ${
                          !selectedCategory ? 'active' : ''
                        }`}
                        onClick={() => {
                          handleCategoryChange('');
                          setIsCategoryDropdownOpen(false);
                        }}
                      >
                        All Categories
                      </li>
                      {categories.map((category) => (
                        <li
                          key={category.id}
                          className={`category-dropdown-item ${
                            selectedCategory === category.name ? 'active' : ''
                          }`}
                          onClick={() => {
                            handleCategoryChange(category.name);
                            setIsCategoryDropdownOpen(false);
                          }}
                        >
                          {category.name}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Price Filter */}
          <div className="filter-section">
            <div className="filter-header" onClick={() => toggleFilter('price')}>
              <span className="filter-title">PRICE</span>
              {expandedFilters.price ? <FaChevronUp /> : <FaChevronDown />}
            </div>
            {expandedFilters.price && (
              <div className="filter-content">
                <div className="price-range">
                  <div className="price-input">
                    <div className="price-input-group">
                      <div className="price-input-wrapper">
                        <span className="currency">₹</span>
                        <input type="number" value={sliderValues.min} readOnly />
                      </div>
                      <span className="price-separator">to</span>
                      <div className="price-input-wrapper">
                        <span className="currency">₹</span>
                        <input type="number" value={sliderValues.max} readOnly />
                        <span className="price-plus">+</span>
                      </div>
                    </div>
                  </div>
                  <div className="range-slider">
                    <div 
                      className="range-track"
                      style={{
                        '--min-percent': `${(sliderValues.min / maxPrice) * 100}`,
                        '--max-percent': `${(sliderValues.max / maxPrice) * 100}`
                      }}
                    ></div>
                    <input
                      type="range"
                      min="0"
                      max={maxPrice}
                      step="10"
                      value={sliderValues.min}
                      name="min"
                      onChange={handleSliderChange}
                      className="range-min"
                    />
                    <input
                      type="range"
                      min="0"
                      max={maxPrice}
                      step="10"
                      value={sliderValues.max}
                      name="max"
                      onChange={handleSliderChange}
                      className="range-max"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right Content Area - Products */}
        <div className="product-content">
          {/* Page Header with Title and Sort */}
          <div className="content-header">
            <div className="page-title-section">
              <h1 className="page-title">{selectedCategory || 'All Products'}</h1>
              <p className="product-count">
                (Showing {filteredItems.length > 0 ? '1' : '0'}-{Math.min(filteredItems.length, 40)} products of {totalItems} products)
              </p>
            </div>
            <div className="sort-section">
              <span className="sort-label">Sort by:</span>
              <div className="sort-options">
                <button 
                  className={`sort-option ${sortBy === 'popularity' ? 'active' : ''}`}
                  onClick={() => setSortBy('popularity')}
                >
                  Popularity
                </button>
                <button 
                  className={`sort-option ${sortBy === 'price-low' ? 'active' : ''}`}
                  onClick={() => setSortBy('price-low')}
                >
                  Price -- Low to High
                </button>
                <button 
                  className={`sort-option ${sortBy === 'price-high' ? 'active' : ''}`}
                  onClick={() => setSortBy('price-high')}
                >
                  Price -- High to Low
                </button>
                <button 
                  className={`sort-option ${sortBy === 'newest' ? 'active' : ''}`}
                  onClick={() => setSortBy('newest')}
                >
                  Newest First
                </button>
                <button 
                  className={`sort-option ${sortBy === 'discount' ? 'active' : ''}`}
                  onClick={() => setSortBy('discount')}
                >
                  Discount
                </button>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          {filteredItems.length > 0 ? (
            <>
              <div className="product-grid">
                {filteredItems.map((item, index) => cardItem(item, index))}
              </div>
              {isLoading && (
                <div className="loading-more">
                  <div className="loading-spinner"></div>
                  Loading more products...
                </div>
              )}
              {!hasMore && filteredItems.length > 0 && (
                <div className="no-more-products">
                  <p>No more products to load</p>
                </div>
              )}
            </>
          ) : searchQuery ? (
            <div className="no-products">
              <div className="no-products-icon">📦</div>
              <h3>No products found</h3>
              <p>No products match your search "<strong>{searchQuery}</strong>". Try different keywords or browse all products.</p>
              <button 
                className="reset-filters"
                onClick={clearSearch}
              >
                Clear Search
              </button>
            </div>
          ) : (
            <div className="no-products">
              <div className="no-products-icon">📦</div>
              <h3>No products found</h3>
              <p>Try adjusting your filters or search for something else</p>
              <button 
                className="reset-filters"
                onClick={() => {
                  setSelectedCategory('');
                  setPriceRange({ min: 0, max: maxPrice });
                  setSliderValues({ min: 0, max: maxPrice });
                }}
              >
                Reset Filters
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Product;